import { createContext, createEffect, Show, Suspense, useContext } from 'solid-js';
import { useAnalytics } from '@troon/analytics';
import { setUser } from '@sentry/solidstart';
import { getRequestEvent, isServer } from 'solid-js/web';
import { query, createAsync, redirect } from '@solidjs/router';
import { getApiClient, getCache } from '@troon/api-client';
import type { ApiResponse } from '@troon/api-client';
import type { AccessorWithLatest } from '@solidjs/router';
import type { ParentProps } from 'solid-js';

export type UserData = ApiResponse<'get', '/v0/auth/me'> & {
	subscription?: ApiResponse<'get', '/v0/troon-access-subscriptions/active'>;
};
type CTX = AccessorWithLatest<UserData | null>;

const User = createContext<CTX>();

export function UserContext(props: ParentProps) {
	// Pre-cache the logged in user from the window to the ttlCache
	// While solid router's `query()` does _some_ caching, the first mutation will automatically invalidate
	// its internal cache. Our custom ttlCache can prevent this from doing unnecessary extra lookups.
	if (!isServer && window.__USER__) {
		getCache()?.set(getLoggedInUser.keyFor(), window.__USER__);
	}
	const user = createAsync(() => getLoggedInUser(), { deferStream: true });
	const analytics = useAnalytics();

	createEffect(() => {
		const data = user();

		if (data) {
			const fullName = `${data.firstName} ${data.lastName}`;
			setUser({ id: data.id, email: data.email, fullName });

			if (!analytics?._isIdentified()) {
				// Important: do not call identify if already identified
				// Failing to do so will add at least n+1 calls to /decide (the feature flag endpoint)
				analytics?.identify(data.id, {
					email: data.email,
					fullName,
					accessSubscription: data.troonAccessProductType,
					rewardsId: data.troonRewardsId,
				});
			}
		} else if (analytics?._isIdentified()) {
			// log out if was previously logged in & identified
			analytics?.reset();
		}
	});

	return (
		<Suspense>
			<Show when={user() !== undefined}>
				<User.Provider value={user as AccessorWithLatest<UserData | null>}>{props.children}</User.Provider>
			</Show>
		</Suspense>
	);
}

export function useUser() {
	const ctx = useContext(User);
	if (!ctx) {
		throw new Error('User context not initialized');
	}
	return ctx;
}

// Manually reimplement lazy/minimal cachedGet
export const getLoggedInUser = query(async (): Promise<UserData | null> => {
	const cacheKey = getLoggedInUser.keyFor();
	const cache = getCache();
	const userData = cache?.get(cacheKey) as UserData;
	if (cache?.has(cacheKey)) {
		return userData!;
	}

	if (isServer) {
		const event = getRequestEvent()!;
		if (event.locals.user) {
			cache?.set(cacheKey, event.locals.user);
			return event.locals.user;
		} else {
			return null;
		}
	}

	try {
		const [user, subscription] = await Promise.all([
			getApiClient().GET('/v0/auth/me'),
			getApiClient().GET('/v0/troon-access-subscriptions/active'),
		]);
		const res = user.data ? { ...user.data, subscription: subscription.data } : null;
		cache?.set(cacheKey, res);
		return res;
	} catch {
		return null;
	}
}, '/v0/auth/me');

export const requireLoggedIn = query(async () => {
	const user = await getLoggedInUser();
	if (user === null) {
		const url = isServer ? new URL(getRequestEvent()!.request.url) : new URL(window.location.toString());
		if (!url.searchParams.get('redirect')) {
			url.searchParams.set('redirect', url.pathname);
		}
		return redirect(`/auth?${url.searchParams.toString()}`, { status: 401 });
	}
	return user;
}, 'require-logged-in-user');
